(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ZugMagnetPricing = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  // Final customer totals: never reapply the sticker material or Acrylic scalar.
  function create(config) {
    if (!config || config.version !== 'magnet_taper_v1' || config.currency !== 'USD') {
      throw new Error('Magnet pricing is unavailable.');
    }
    ['custom', 'acrylic'].forEach(function (key) {
      var family = config.families && config.families[key];
      if (!family || !Array.isArray(family.rows) || !family.rows.length) throw new Error('Missing magnet tiers.');
      var expected = key === 'custom' ? [1599, 15, 1, 40000] : [1300, 3, 5, 5000];
      if (family.minimumTotalCents !== expected[0] || family.minimumQuantity !== expected[1] ||
          family.quantityStep !== expected[2] || family.maximumQuantity !== expected[3]) {
        throw new Error('Invalid magnet quantity or minimum-total policy.');
      }
      var previousArea = 0;
      if (family.interpolateAreaFrom !== undefined &&
          (key !== 'custom' || family.interpolateAreaFrom !== 144 ||
           !family.rows.some(function (row) { return row.area === 144; }))) {
        throw new Error('Invalid magnet area interpolation policy.');
      }
      family.rows.forEach(function (row) {
        if (!Number.isFinite(row.area) || !(row.area > previousArea) || !Array.isArray(row.anchors) || !row.anchors.length) throw new Error('Invalid magnet size tier.');
        previousArea = row.area;
        var previous = null;
        row.anchors.forEach(function (anchor) {
          if (!Array.isArray(anchor) || anchor.length !== 2 || !Number.isSafeInteger(anchor[0]) || !Number.isSafeInteger(anchor[1]) ||
              anchor[0] < family.minimumQuantity || anchor[0] > family.maximumQuantity ||
              (key === 'acrylic' && anchor[0] !== 3 && anchor[0] % 5 !== 0) || anchor[1] < family.minimumTotalCents ||
              (previous && (anchor[0] <= previous[0] || anchor[1] * previous[0] > previous[1] * anchor[0]))) {
            throw new Error('Magnet tiers must have non-increasing unit prices.');
          }
          previous = anchor;
        });
        if (row.anchors[0][0] !== family.minimumQuantity ||
            previous[0] !== family.maximumQuantity) throw new Error('Incomplete magnet quantity range.');
      });
    });
    // Custom dimensions have unlimited possible areas. Retain only the most
    // recent configurations rather than accumulating a quantity table per edit.
    var cache = new Map();

    function quote(familyKey, width, height, pieces, multiplier, rateCents, maximumEncodedQuantity) {
      var family = config.families[familyKey];
      if (!family || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 ||
          !Number.isSafeInteger(pieces) || pieces < family.minimumQuantity || pieces > family.maximumQuantity ||
          (familyKey === 'acrylic' && pieces !== 3 && pieces % 5 !== 0) ||
          !Number.isFinite(multiplier) || multiplier < 1 ||
          !Array.isArray(rateCents) || !rateCents.length ||
          !rateCents.every(function (r) { return Number.isSafeInteger(r) && r > 0; }) ||
          !Number.isSafeInteger(maximumEncodedQuantity) || maximumEncodedQuantity <= 0) {
        throw new Error('Invalid magnet pricing input or native-rate catalog.');
      }
      var area = width * height;
      var row = family.rows.filter(function (candidate) { return candidate.area + 1e-9 >= area; })[0] ||
        family.rows[family.rows.length - 1];
      var lowerRow = row;
      var areaFraction = 0;
      if (family.interpolateAreaFrom && area >= family.interpolateAreaFrom && area < row.area) {
        lowerRow = family.rows[family.rows.indexOf(row) - 1] || row;
        if (lowerRow !== row) areaFraction = (area - lowerRow.area) / (row.area - lowerRow.area);
      }
      var rates = rateCents.slice().sort(function (a, b) { return a - b; });
      var key = [familyKey, lowerRow === row ? row.area : area, multiplier, maximumEncodedQuantity, rates.join(',')].join('|');
      var entry = cache.get(key) || { totals: Object.create(null), lastQuantity: 0, lastTotal: 0 };
      cache.delete(key);
      cache.set(key, entry);
      if (cache.size > 24) cache.delete(cache.keys().next().value);
      var anchors = row.anchors;

      function bounds(q, table) {
        table = table || anchors;
        var high = 0;
        while (table[high][0] < q) high += 1;
        return [table[high][0] === q ? high : high - 1, high];
      }
      function quantityTotal(table, q) {
        var indexes = bounds(q, table);
        var low = table[indexes[0]], high = table[indexes[1]];
        return low[0] === high[0] ? low[1] : low[1] + (high[1] - low[1]) * (q - low[0]) / (high[0] - low[0]);
      }
      function nativeTotalAtMost(ceiling) {
        var best = 0;
        for (var i = 0; i < rates.length; i += 1) {
          var rate = rates[i];
          var total = Math.min(Math.floor(ceiling / rate), maximumEncodedQuantity) * rate;
          if (total > best) best = total;
          if (best === ceiling) break;
        }
        if (best < family.minimumTotalCents) throw new Error('Magnet price has no safe native representation.');
        return best;
      }
      // Enforce the cap on the CHARGED amount at every supported quantity, not
      // just the unrounded anchors. This also prevents native-rate/currency
      // rounding from reintroducing a unit-price increase between tiers.
      while (entry.lastQuantity < pieces) {
        var q = entry.lastQuantity === 0 ? family.minimumQuantity :
          (familyKey === 'acrylic' && entry.lastQuantity === 3 ? 5 : entry.lastQuantity + family.quantityStep);
        var total = quantityTotal(anchors, q);
        if (lowerRow !== row) {
          var lowerTotal = quantityTotal(lowerRow.anchors, q);
          total = lowerTotal + (total - lowerTotal) * areaFraction;
        }
        var ceiling = Math.round(Math.max(family.minimumTotalCents, total * multiplier));
        if (entry.lastQuantity) ceiling = Math.min(ceiling, Math.floor(entry.lastTotal * q / entry.lastQuantity + 1e-7));
        entry.lastTotal = nativeTotalAtMost(ceiling);
        entry.lastQuantity = q;
        entry.totals[q] = entry.lastTotal;
      }
      var selectedBounds = bounds(pieces);
      return {
        version: config.version,
        totalCents: entry.totals[pieces],
        unitPrice: entry.totals[pieces] / 100 / pieces,
        area: area,
        sizeTier: row.area,
        qLow: anchors[selectedBounds[0]][0],
        qHigh: anchors[selectedBounds[1]][0]
      };
    }
    return { quote: quote };
  }
  return { create: create };
});
